import { useAuiState } from '@assistant-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

/**
 * Runtime → URL sync. Subscribes to the active thread's remoteId and
 * navigates `/chat/<id>` whenever it diverges from the URL (e.g. user
 * clicks a different thread in the sidebar). The URL → runtime side is
 * already handled by `useRemoteThreadListRuntime({threadId})`; this
 * closes the loop so a hard refresh always lands on the thread the user
 * thought they were on.
 *
 * Uses `replace:true` so the URL bar updates without bloating browser
 * history (clicking 5 threads doesn't leave 5 back-button stops). On
 * initial mint the existing `window.history.replaceState` in
 * `onInitialized` still wins (fires before TanStack settles), this
 * effect is the ongoing-sync mechanism after that.
 */
export function ActiveThreadUrlSync({ urlThreadId }: { urlThreadId: string | null }) {
  const navigate = useNavigate();
  const remoteId = useAuiState((s) => s.threadListItem.remoteId) as string | undefined;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  useEffect(() => {
    if (!remoteId) return;
    if (remoteId === urlThreadId) return;
    void navigateRef.current({
      to: '/chat/$threadId',
      params: { threadId: remoteId },
      replace: true,
    });
  }, [remoteId, urlThreadId]);
  return null;
}
